import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { GuidePanel, ContextHelpButton, OnboardingTour, shouldShowInitialTour } from "../components/guide/GuideSystem";
import { pageKeyByPath, type GuidePageKey } from "../config/guideContent";
import { Icon } from "../components/ui/Icon";
import { ActionButton, AlertCard, EmptyState, ErrorState, LoadingState, MetricCard, PageHeader, SearchInput, StatusPill } from "../components/operations/OperationalComponents";
import { CommandPalette } from "../components/command/CommandPalette";
import * as Legacy from "../app/runtime";

const { localPreviewPorts, apiBaseUrl, http, getUser, setAuth, clearAuth, getErr, fmt, fmtDate, fmtDateFull, fmtTime, fmtRel, urgColor, urgLabel, todayStr, startOfDayIso, endOfDayIso, genKey, safePhone, waUrl, mapsUrl, telUrl, openExternal, BRAND_LOGO, TOK, C, EXECUTIVE_ROLES, MANAGER_ROLES, SALES_ROLES, OPS_ROLES, ADMIN_ROLES, WHATSAPP_ROLES, FOLLOW_UP_ROLES, ROLE_LABELS, roleLabel, hasAnyRole, CSS, BrandLogo, ICON_NAMES, renderIcon, Btn, Input, Card, Modal, Toast, Skeleton, Badge, StatusBadge, KPI, SH, Tabs, Row, Empty, useToast, QO, useOrders, useOrder, useCustSrch, useCust, useNotifs, useRules, useCal, useMemory, useFollowUps, useRevenue, useRisks, useStaff, useFeedback, useAssist, useHealth, useUsers, useApprovals, useAudit, useDelivery, useInvoices, useMktRpt, useCharge, useSearch, useInquiries, useGST, useFollowUpTasks, useWhatsAppConversations, useWhatsAppConversation, useWhatsAppAssignableUsers, GlobalSearch, NotifBell, PIPE, StatusPipeline, CancelModal, RescheduleModal, RiderModal, OrderCommandCenter, NewOrderForm, OrderCard, LOST_REASONS, NewInquiryForm, InquiryConversationModal, QuickActions, NAV_MAP } = Legacy;

export const WhatsAppHubPage = () => {
  const user = getUser();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { t, show } = useToast();
  const canUseHub = WHATSAPP_ROLES.includes(user?.role);
  const canAssign = ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN", "OPERATIONS_MANAGER", "SALES_MANAGER"].includes(user?.role);
  const canAdminWhatsApp = ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN"].includes(user?.role);
  const [status, setStatus] = useState("");
  const [queue, setQueue] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [assignReason, setAssignReason] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [mobileView, setMobileView] = useState<"list" | "conversation">("list");
  const [contextOpen, setContextOpen] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);
  const queueStatus: Record<string, string> = {
    "Waiting for RM": "WAITING_FOR_RM",
    "Waiting for Customer": "WAITING_FOR_CUSTOMER",
    Resolved: "RESOLVED",
  };
  const params = {
    status: status || queueStatus[queue] || undefined,
    assignedToMe: queue === "Assigned to me" ? "true" : undefined,
    unreadOnly: queue === "Unread" ? "true" : undefined,
    search: search || undefined,
    limit: 50,
  };
  const { data, isLoading, refetch } = useWhatsAppConversations(params);
  const { data: detail, refetch: refetchDetail, isFetching: detailLoading } = useWhatsAppConversation(selected);
  const { data: assignableUsers } = useWhatsAppAssignableUsers(canAssign);
  const { data: connectionState, refetch: refetchConnection } = useQuery({
    queryKey: ["wa-connection"],
    queryFn: () => http.get("/whatsapp/connection").then((r: any) => r.data.data),
    enabled: canAdminWhatsApp,
    staleTime: 15000,
    retry: false,
  });
  const items = data?.items || [];
  const active = detail || items.find((c: any) => c.id === selected);

  useEffect(() => {
    if (!selected && items.length) setSelected(items[0].id);
  }, [items, selected]);

  if (!canUseHub) {
    return <Card><EmptyState icon="shield-check" title="Conversation Hub is restricted" description="WhatsApp customer conversations are only visible to owners, managers, and Sales/RM roles." /></Card>;
  }

  const waitingTone = (minutes?: number | null) => {
    if (minutes == null) return C.light;
    if (minutes >= 120) return C.danger;
    if (minutes >= 30) return C.warning;
    return C.success;
  };
  const waitingLabel = (minutes?: number | null) => {
    if (minutes == null) return "No customer wait";
    if (minutes < 60) return `${minutes}m waiting`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m waiting`;
  };
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["wa-conversations"] });
    if (selected) qc.invalidateQueries({ queryKey: ["wa-conversation", selected] });
  };
  const markSeen = async () => {
    if (!active?.id) return;
    try {
      await http.patch(`/whatsapp/conversations/${active.id}/human-seen`);
      invalidate(); refetch(); refetchDetail();
      show("Marked human seen");
    } catch (e: any) { show(getErr(e), "error"); }
  };
  const toggleTakeover = async () => {
    if (!active?.id) return;
    try {
      await http.patch(`/whatsapp/conversations/${active.id}/human-takeover`, { humanTakeover: !active.humanTakeover });
      invalidate(); refetchDetail();
      show(!active.humanTakeover ? "Human takeover enabled" : "Human takeover released");
    } catch (e: any) { show(getErr(e), "error"); }
  };
  const assignRm = async () => {
    if (!active?.id || !assignTo) return;
    try {
      await http.patch(`/whatsapp/conversations/${active.id}/assign`, { assignedRmId: assignTo, reason: assignReason || undefined });
      setAssignTo(""); setAssignReason("");
      invalidate(); refetchDetail();
      show("RM assigned");
    } catch (e: any) { show(getErr(e), "error"); }
  };
  const sendReply = async () => {
    if (!active?.id) return;
    const body = replyBody.trim();
    if (!body) { show("Type a message before sending", "error"); return; }
    setReplySending(true);
    try {
      await http.post(`/whatsapp/conversations/${active.id}/messages`, { body });
      setReplyBody("");
      invalidate(); refetch(); refetchDetail();
      show("WhatsApp reply sent");
    } catch (e: any) { show(getErr(e), "error"); }
    finally { setReplySending(false); }
  };

  const loadFacebookSdk = async (appId: string, graphVersion: string) => {
    const w = window as any;
    if (w.FB) return w.FB;
    await new Promise<void>((resolve, reject) => {
      const existing = document.getElementById("facebook-jssdk");
      w.fbAsyncInit = () => {
        w.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version: graphVersion });
        resolve();
      };
      if (existing) {
        const timer = window.setInterval(() => { if (w.FB) { window.clearInterval(timer); resolve(); } }, 100);
        window.setTimeout(() => { window.clearInterval(timer); if (!w.FB) reject(new Error("Meta SDK failed to load")); }, 10000);
        return;
      }
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.async = true; script.defer = true; script.crossOrigin = "anonymous";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.onerror = () => reject(new Error("Meta SDK failed to load"));
      document.body.appendChild(script);
    });
    return w.FB;
  };

  const connectExistingWhatsApp = async () => {
    const meta = connectionState?.meta;
    if (!meta?.appId || !meta?.configId) { show("Meta Embedded Signup is not configured on the backend", "error"); return; }
    setConnectBusy(true);
    try {
      const FB = await loadFacebookSdk(meta.appId, meta.graphVersion);
      let finishPayload: any = null;
      let resolveSession: (value: any) => void = () => {};
      const sessionPromise = new Promise<any>((resolve) => { resolveSession = resolve; });
      const listener = (event: MessageEvent) => {
        if (event.origin !== "https://www.facebook.com" && !event.origin.endsWith(".facebook.com")) return;
        try {
          const payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
          if (payload?.type !== "WA_EMBEDDED_SIGNUP") return;
          if (["FINISH", "FINISH_ONLY_WABA", "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"].includes(payload.event)) {
            finishPayload = payload; resolveSession(payload);
          }
        } catch { /* ignore unrelated Meta messages */ }
      };
      window.addEventListener("message", listener);

      await new Promise<void>((resolve, reject) => {
        FB.login((response: any) => {
          void (async () => {
            try {
            const code = response?.authResponse?.code;
            if (!code) throw new Error(response?.status === "unknown" ? "Meta signup was cancelled" : "Meta did not return an exchange code");
            if (!finishPayload) {
              finishPayload = await Promise.race([sessionPromise, new Promise((r) => window.setTimeout(() => r(null), 12000))]);
            }
            const wabaId = finishPayload?.data?.waba_id;
            const phoneNumberId = finishPayload?.data?.phone_number_id;
            if (!wabaId) throw new Error("Meta completed login but did not return the WABA ID");
            await http.post("/whatsapp/connection/complete", { code, wabaId, phoneNumberId, event: finishPayload?.event });
            await refetchConnection();
            await refetch();
            show("Existing WhatsApp Business connected");
            resolve();
            } catch (error) { reject(error); }
            finally { window.removeEventListener("message", listener); }
          })();
        }, {
          config_id: meta.configId,
          auth_type: "rerequest",
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "whatsapp_business_app_onboarding",
            sessionInfoVersion: "3",
          },
        });
      });
    } catch (e: any) { show(getErr(e), "error"); }
    finally { setConnectBusy(false); }
  };

  const unreadTotal = items.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0);
  const attentionCount = items.filter((c: any) => c.needsHumanAttention).length;
  const unassignedCount = items.filter((c: any) => !c.assignedRmId).length;
  const messages = [...(detail?.messages || [])].reverse();
  const activeWaitingMinutes = active?.waitingMinutes ?? (active?.lastCustomerMessageAt ? Math.max(0, Math.floor((Date.now() - new Date(active.lastCustomerMessageAt).getTime()) / 60000)) : null);
  const sendBlocked = !active || ["RESOLVED", "ARCHIVED", "BLOCKED"].includes(active.status);
  const contextPanel = active ? (
    <div>
      <SH title="CRM Context" />
      <dl className="wa-context-dl">
        <dt>Customer</dt><dd>{active.customer?.name || "Not linked"}</dd>
        <dt>Phone</dt><dd>+{active.phone}</dd>
        <dt>Inquiry</dt><dd title={active.inquiry?.inquiryNumber}>{active.inquiry ? `${active.inquiry.inquiryNumber} · ${active.inquiry.status}` : "Not linked"}</dd>
        <dt>Assigned RM</dt><dd>{active.assignedRm?.name || "Unassigned"}</dd>
        <dt>Customer Msg</dt><dd>{active.lastCustomerMessageAt ? `${fmtRel(active.lastCustomerMessageAt)} · ${fmtDateFull(active.lastCustomerMessageAt)}` : "—"}</dd>
        <dt>Human Seen</dt><dd>{active.lastHumanSeenAt ? fmtRel(active.lastHumanSeenAt) : "Not seen"}</dd>
        <dt>Assistant</dt><dd>{active.aiState?.mode || "OFF"}</dd>
      </dl>
      {canAssign && (
        <div style={{ marginTop: 16, background: C.surface, borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
          <SH title="Assign RM" />
          <select
            aria-label="Assign relationship manager"
            value={assignTo}
            onChange={e =>
              setAssignTo(e.target.value)
            }
            style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: 13, fontFamily: "inherit", background: C.surface, color: C.text, outline: "none", boxSizing: "border-box" as any, marginBottom: 10 }}
          >
            <option value="">Select RM…</option>
            {(assignableUsers || []).map((u: any) => <option key={u.id} value={u.id}>{u.name} · {roleLabel(u.role)}</option>)}
          </select>
          <Input
            label="Assignment reason"
            value={assignReason}
            onChange={setAssignReason}
            placeholder="Reason, optional"
          />
          <Btn size="sm" full disabled={!assignTo} onClick={assignRm}>Assign</Btn>
        </div>
      )}
      {(active.assignments || []).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <SH title="Assignment History" />
          {active.assignments.slice(0, 4).map((a: any) => (
            <div key={a.id} className="cc-text-wrap" style={{ fontSize: 12, color: C.mid, marginBottom: 8, lineHeight: 1.45 }}>
              <strong style={{ color: C.text }}>{a.assignedToUser?.name}</strong> · {a.createdAt ? fmtRel(a.createdAt) : ""}
              {a.reason && <div>{a.reason}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div>
      {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}
      <PageHeader
        eyebrow="Governed WhatsApp CRM"
        title="Conversation Hub"
        subtitle="Inbound messages are captured for humans. Review context, assign ownership, and send only deliberate manual replies."
        actions={<StatusPill tone="warning">No auto-send</StatusPill>}
      />
      {canAdminWhatsApp && (
        <Card style={{ marginBottom: 18, padding: 16, border: `1px solid ${connectionState?.connection?.connectionStatus === "CONNECTED" ? C.success : C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" as any }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 900, color: C.text }}>WhatsApp Business connection</div>
              <div style={{ fontSize: 12, color: C.mid, marginTop: 4 }}>
                {connectionState?.connection?.connectionStatus === "CONNECTED"
                  ? `${connectionState.connection.verifiedName || "WhatsApp Business"} · ${connectionState.connection.displayPhoneNumber || connectionState.connection.phoneNumberId}`
                  : connectionState?.configured === false ? "Meta Embedded Signup configuration is incomplete" : "Connect the existing WhatsApp Business App number using Meta coexistence."}
              </div>
              {connectionState?.connection?.connectionStatus === "CONNECTED" && (
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" as any }}>
                  <StatusPill tone="success">Connected</StatusPill>
                  <StatusPill tone={connectionState.connection.coexistenceEnabled ? "success" : "warning"}>{connectionState.connection.coexistenceEnabled ? "Coexistence enabled" : "Cloud API only"}</StatusPill>
                  <StatusPill tone={connectionState.connection.lastWebhookAt ? "success" : "neutral"}>{connectionState.connection.lastWebhookAt ? "Webhook active" : "Waiting for first webhook"}</StatusPill>
                </div>
              )}
            </div>
            <Btn onClick={connectExistingWhatsApp} disabled={connectBusy || connectionState?.configured === false}>
              {connectBusy ? "Connecting…" : connectionState?.connection?.connectionStatus === "CONNECTED" ? "Reconnect WhatsApp" : "Connect Existing WhatsApp"}
            </Btn>
          </div>
        </Card>
      )}
      <div className="op-grid-4" style={{ marginBottom: 18 }}>
        <MetricCard label="Visible Chats" value={data?.total ?? items.length} tone={C.primary} icon="message-circle" detail="current filter" />
        <MetricCard
          label="Unread"
          value={
            isLoading ? "…" : unreadTotal
          }
          tone={
            !isLoading && unreadTotal
              ? C.danger
              : C.success
          }
          icon="bell"
          detail={
            isLoading
              ? "Loading…"
              : "needs human read"
          }
        />

        <MetricCard
          label="Needs RM"
          value={
            isLoading
              ? "…"
              : attentionCount
          }
          tone={
            !isLoading && attentionCount
              ? C.warning
              : C.success
          }
          icon="clock-alert"
          detail={
            isLoading
              ? "Loading…"
              : "waiting ownership"
          }
        />

        <MetricCard
          label="Unassigned"
          value={
            isLoading
              ? "…"
              : unassignedCount
          }
          tone={
            !isLoading && unassignedCount
              ? C.info
              : C.success
          }
          icon="users"
          detail={
            isLoading
              ? "Loading…"
              : "assign to RM"
          }
        />
      </div>
      <AlertCard tone="info" title="Manual-only customer messaging">Webhook received does not equal reply. This hub has no AI send, template send, campaign send, or auto-follow-up control.</AlertCard>

      <div className="wa-workspace">
        <div className={`wa-panel wa-list-panel ${mobileView === "conversation" ? "wa-hide-on-mobile" : ""}`}>
        <Card style={{ padding: 14, minHeight: 620 }}>
          <SH title="Conversation Inbox" action={<Btn size="xs" v="ghost" onClick={() => refetch()}>Refresh</Btn>} />
          <div className="op-grid-2" style={{ gap: 8, marginBottom: 10 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search phone or customer" />
            <Input label="" value={status} onChange={setStatus} options={["OPEN", "WAITING_FOR_RM", "WAITING_FOR_CUSTOMER", "RESOLVED", "ARCHIVED", "BLOCKED"]} />
          </div>
          <Tabs items={["All", "Assigned to me", "Unread", "Waiting for RM", "Waiting for Customer", "Resolved"]} active={queue} onChange={setQueue} />

          {isLoading && <LoadingState rows={4} />}
          {!isLoading && !items.length && <EmptyState icon="message-circle" title="No WhatsApp conversations" description="New inbound WhatsApp messages will appear here after the governed webhook stores them." />}
          <div className="wa-card-scroll" style={{ display: "flex", flexDirection: "column" as any, gap: 10 }}>
            {items.map((c: any) => {
              const activeRow = selected === c.id;
              const waitColor = waitingTone(c.waitingMinutes);
              return (
                <button key={c.id} onClick={() => { setSelected(c.id); setMobileView("conversation"); setContextOpen(false); }} style={{ textAlign: "left" as any, border: `1.5px solid ${activeRow ? C.primary : C.border}`, background: activeRow ? C.soft : C.surface, borderRadius: 16, padding: 14, cursor: "pointer", fontFamily: "inherit", boxShadow: activeRow ? `0 10px 28px ${C.primary}18` : "none", width: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <strong className="cc-truncate" title={c.customer?.name || c.inquiry?.customerName || c.phone} style={{ fontSize: 14, color: C.text }}>{c.customer?.name || c.inquiry?.customerName || c.phone}</strong>
                        {c.unreadCount > 0 && <Badge color={C.danger}>{c.unreadCount} unread</Badge>}
                      </div>
                      <div className="cc-truncate" style={{ fontSize: 12, color: C.mid, marginBottom: 5 }}>+{c.phone} · {c.assignedRm?.name || "Unassigned"}</div>
                      <div style={{ fontSize: 12, color: C.text, opacity: .82, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as any }}>{c.lastMessagePreview || "No message preview"}</div>
                    </div>
                    <div style={{ textAlign: "right" as any, flexShrink: 0 }}>
                      <StatusPill tone={c.humanTakeover ? "info" : c.status === "WAITING_FOR_RM" ? "warning" : c.status === "RESOLVED" ? "success" : "neutral"}>{c.humanTakeover ? "Human" : c.status.replace(/_/g, " ")}</StatusPill>
                      <div style={{ fontSize: 10, color: waitColor, fontWeight: 800, marginTop: 7 }}>{waitingLabel(c.waitingMinutes)}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" as any }}>
                    {c.inquiry && <span className="cc-truncate" title={c.inquiry.inquiryNumber} style={{ maxWidth: 130, display: "inline-block" }}><Badge color={C.secondary}>{c.inquiry.inquiryNumber}</Badge></span>}
                    <Badge color={c.lastMessage?.humanSeen ? C.success : C.warning}>{c.lastMessage?.humanSeen ? "Seen by team" : "Not human seen"}</Badge>
                    {c.lastMessage?.aiProcessed && <Badge color={C.purple}>AI processed</Badge>}
                    {c.lastMessageAt && <span className="cc-truncate" title={fmtDateFull(c.lastMessageAt)} style={{ fontSize: 11, color: C.light, marginLeft: "auto", maxWidth: 110 }}>{fmtRel(c.lastMessageAt)}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
        </div>

        <div className={`wa-panel wa-timeline-panel ${mobileView === "list" ? "wa-hide-on-mobile" : ""}`}>
        <Card style={{ padding: 0, overflow: "hidden", minHeight: 620 }}>
          {!active ? <EmptyState icon="message-circle" title="Select a conversation" description="Choose a WhatsApp inquiry from the inbox to inspect the CRM context and timeline." /> : (
            <>
              <div style={{ padding: 20, borderBottom: `1px solid ${C.border}`, background: `linear-gradient(135deg,${C.surface},${C.bg})` }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0 }}>
                    <span className="wa-mobile-only" style={{ marginBottom: 10 }}>
                      <Btn size="xs" v="ghost" onClick={() => setMobileView("list")}>Back to inbox</Btn>
                    </span>
                    <div className="cc-truncate" title={active.customer?.name || active.inquiry?.customerName || active.phone} style={{ fontSize: 22, fontWeight: 900, color: C.primary, marginBottom: 4 }}>{active.customer?.name || active.inquiry?.customerName || active.phone}</div>
                    <div className="cc-truncate" title={active.waId || "not provided"} style={{ fontSize: 13, color: C.mid }}>+{active.phone} · WA ID {active.waId || "not provided"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as any, justifyContent: "flex-end" }}>
                    <Badge color={active.unreadCount > 0 ? C.danger : C.success}>{active.unreadCount || 0} unread</Badge>
                    <Badge color={active.humanTakeover ? C.primary : C.light}>{active.humanTakeover ? "Human takeover" : "Standard flow"}</Badge>
                    <Badge color={waitingTone(activeWaitingMinutes)}>{waitingLabel(activeWaitingMinutes)}</Badge>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" as any }}>
                  <Btn size="sm" onClick={markSeen} disabled={!active.unreadCount}>Mark human seen</Btn>
                  <Btn size="sm" v={active.humanTakeover ? "ghost" : "secondary"} onClick={toggleTakeover}>{active.humanTakeover ? "Release takeover" : "Human takeover"}</Btn>
                  <Btn size="sm" v="ghost" onClick={() => refetchDetail()}>Refresh</Btn>
                  {active.customer?.id && <Btn size="sm" v="ghost" onClick={() => nav("/customers")}>Open customer</Btn>}
                  {active.inquiry?.id && <Btn size="sm" v="ghost" onClick={() => nav("/orders")}>Open inquiry</Btn>}
                  <span className="wa-tablet-context-button"><Btn size="sm" v="ghost" onClick={() => setContextOpen(true)}>Customer context</Btn></span>
                </div>
              </div>

              <div style={{ minHeight: 520 }}>
                <div style={{ padding: 18 }}>
                  <SH title="Message Timeline" action={detailLoading ? <span style={{ fontSize: 11, color: C.light }}>Refreshing…</span> : null} />
                  {!messages.length && <EmptyState icon="clipboard-list" title="No stored messages" description="Inbound WhatsApp messages will appear after webhook intake." />}
                  <div style={{ display: "flex", flexDirection: "column" as any, gap: 12 }}>
                    {messages.map((m: any) => {
                      const inbound = m.direction === "INBOUND";
                      return (
                        <div key={m.id} style={{ display: "flex", justifyContent: inbound ? "flex-start" : "flex-end" }}>
                          <div className="wa-message-bubble" style={{ background: inbound ? C.bg : C.soft, border: `1px solid ${inbound ? C.border : C.accent}`, borderRadius: inbound ? "18px 18px 18px 6px" : "18px 18px 6px 18px", padding: "11px 13px" }}>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as any, marginBottom: 6 }}>
                              <Badge color={inbound ? C.info : C.primary}>{m.direction}</Badge>
                              <Badge color={C.mid}>{m.messageType}</Badge>
                              <Badge color={m.status === "FAILED" ? C.danger : C.success}>{m.status}</Badge>
                            </div>
                            <div className="cc-text-wrap" style={{ fontSize: 13, color: C.text, lineHeight: 1.55 }}>{m.body || `[${m.messageType} message]`}</div>
                            {m.mediaUrl && <div style={{ marginTop: 6, fontSize: 11, color: C.mid }}>Media reference stored</div>}
                            <div className="wa-message-meta">
                              <span style={{ fontSize: 10, color: C.light }}>{m.createdAt ? fmtRel(m.createdAt) : ""}</span>
                              <Badge color={m.humanSeen ? C.success : C.warning}>{m.humanSeen ? "human seen" : "not human seen"}</Badge>
                              <Badge color={m.aiProcessed ? C.purple : C.light}>{m.aiProcessed ? "assistant processed" : "assistant untouched"}</Badge>
                            </div>
                            {(m.statuses || []).length > 0 && (
                              <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
                                {(m.statuses || []).slice(0, 3).map((s: any) => <span key={s.id} style={{ fontSize: 10, color: C.mid, marginRight: 8 }}>{s.status} · {s.timestamp ? fmtRel(s.timestamp) : ""}</span>)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 18, padding: 14, borderRadius: 18, border: `1px solid ${C.border}`, background: C.bg }}>
                    <SH title="Manual WhatsApp Reply" action={<Badge color={C.danger}>Human send only</Badge>} />
                    <textarea value={replyBody} onChange={e => setReplyBody(e.target.value)} disabled={replySending || sendBlocked} placeholder={sendBlocked ? "Conversation is not open for replies" : "Type the exact WhatsApp message to send manually…"} rows={4} maxLength={4000} style={{ width: "100%", padding: "12px 14px", border: `1.5px solid ${C.border}`, borderRadius: 14, fontSize: 14, fontFamily: "inherit", background: sendBlocked ? "#f7f2ef" : C.surface, color: C.text, outline: "none", resize: "vertical", boxSizing: "border-box" as any, marginBottom: 10 }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" as any }}>
                      <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.45 }}>One click sends one message to this customer. No AI, no template, no campaign, no auto-send.</div>
                      <Btn onClick={sendReply} loading={replySending} disabled={sendBlocked || !replyBody.trim()} icon="message-circle">Send WhatsApp</Btn>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>
        </div>
        <div className="wa-panel wa-context-panel">
          <Card style={{ padding: 18, minHeight: 620, background: C.bg }}>
            {contextPanel || <EmptyState icon="users" title="Customer context" description="Select a conversation to see linked customer, inquiry, and RM ownership details." />}
          </Card>
        </div>
      </div>
      {contextOpen && active && (
        <div className="wa-context-drawer-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setContextOpen(false); }}>
          <aside className="wa-context-drawer" aria-label="Customer context drawer">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <SH title="Customer Context" />
              <button aria-label="Close customer context" onClick={() => setContextOpen(false)} style={{ width: 34, height: 34, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg, color: C.mid, cursor: "pointer", display: "grid", placeItems: "center" }}>×</button>
            </div>
            {contextPanel}
          </aside>
        </div>
      )}
    </div>
  );
};

export default WhatsAppHubPage;
